/**
 * The complaint category vocabulary is deliberately independent from UI copy.
 * Stored codes are stable evidence; providers may only change presentation.
 */
export type ComplaintCategory = {
  code: string;
  label: string;
  retired?: boolean;
  replacementCode?: string;
};

export type ComplaintCategorySet = { categories: ComplaintCategory[] };

export type EffectiveComplaintCategory = ComplaintCategory & { active: boolean };

export type LegacyCategoryNormalisation = {
  code: string;
  originalText?: string;
};

const canonicalPairs = [
  ['PHYSICAL_ABUSE', 'Physical Abuse'],
  ['SEXUAL_ABUSE', 'Sexual Abuse'],
  ['PSYCHOLOGICAL_ABUSE', 'Psychological Abuse'],
  ['FINANCIAL_ABUSE', 'Financial Abuse'],
  ['NEGLECT', 'Neglect'],
  ['USE_OF_FORCE', 'Use of Force'],
  ['UNLAWFUL_RESTRAINT', 'Unlawful Restraint'],
  ['UNLAWFUL_SEXUAL_CONTACT', 'Unlawful Sexual Contact'],
  ['UNEXPECTED_DEATH', 'Unexpected Death'],
  ['MISSING_CONSUMER', 'Missing Consumer'],
  ['CARE_QUALITY', 'Care Quality'],
  ['STAFF_CONDUCT', 'Staff Conduct'],
  ['COMMUNICATION', 'Communication'],
  ['MEDICATION', 'Medication'],
  ['SAFETY_SECURITY', 'Safety & Security'],
  ['DIGNITY_RESPECT', 'Dignity & Respect'],
  ['FOOD_NUTRITION', 'Food & Nutrition'],
  ['ENVIRONMENT', 'Environment'],
  ['BILLING_FINANCE', 'Billing & Finance'],
  ['PRIVACY', 'Privacy'],
  ['ACCESS_TO_SERVICES', 'Access to Services'],
  ['OTHER', 'Other'],
] as const;

/** The platform baseline is cloned before returning so callers cannot mutate it. */
export const DEFAULT_COMPLAINT_CATEGORY_SET: ComplaintCategorySet = {
  categories: canonicalPairs.map(([code, label]) => ({ code, label })),
};

const literalToCode = new Map<string, string>([
  ...canonicalPairs.map(([code, label]) => [label, code] as [string, string]),
  ...canonicalPairs.map(([code]) => [code.toLowerCase(), code] as [string, string]),
  ['General', 'OTHER'],
  ['Uncategorised', 'OTHER'],
]);

const copyDefault = (): ComplaintCategorySet => ({
  categories: DEFAULT_COMPLAINT_CATEGORY_SET.categories.map((category) => ({ ...category })),
});

const isCategory = (candidate: unknown): candidate is ComplaintCategory =>
  !!candidate &&
  typeof candidate === 'object' &&
  typeof (candidate as ComplaintCategory).code === 'string' &&
  typeof (candidate as ComplaintCategory).label === 'string';

/**
 * Turns malformed stored JSON into the safe platform list. Valid retired entries
 * remain reportable but are excluded from new-entry pickers by `active`.
 */
export function resolveComplaintCategories(settingValue: unknown): EffectiveComplaintCategory[] {
  const candidate = settingValue as ComplaintCategorySet | undefined;
  const categories = Array.isArray(candidate?.categories)
    ? candidate.categories
    : copyDefault().categories;
  const valid = validateComplaintCategoryWrite({ categories });
  if (!valid.ok) {
    return copyDefault().categories.map((category) => ({ ...category, active: true }));
  }
  return categories.map((category) => ({
    ...category,
    label: category.label.trim(),
    active: category.retired !== true,
  }));
}

/** Never exposes an opaque historic token outside authorised administration views. */
export function labelForComplaintCategory(
  code: string | null | undefined,
  effective: readonly EffectiveComplaintCategory[],
  options: { adminView?: boolean } = {},
): string {
  const original = code?.trim() ?? '';
  const known = effective.find((category) => category.code === original);
  if (known) return known.label;
  return options.adminView && original ? `Other (legacy: ${original})` : 'Other';
}

/**
 * Covers every literal emitted by the historical picker, the SIRS banner and
 * the public-feedback bridge. Unknown historic text is retained separately.
 */
export function normaliseLegacyCategory(
  text: string | null | undefined,
): LegacyCategoryNormalisation {
  const original = text?.trim() ?? '';
  const code = literalToCode.get(original) ?? literalToCode.get(original.toLowerCase());
  return code ? { code } : { code: 'OTHER', ...(original ? { originalText: original } : {}) };
}

/** Validates a T3 category write before it reaches the controlled settings RPC. */
export function validateComplaintCategoryWrite(
  proposed: unknown,
): { ok: true; value: ComplaintCategorySet } | { ok: false; message: string } {
  if (
    !proposed ||
    typeof proposed !== 'object' ||
    !Array.isArray((proposed as ComplaintCategorySet).categories)
  ) {
    return { ok: false, message: 'Provide a category list.' };
  }
  const categories = (proposed as ComplaintCategorySet).categories;
  if (!categories.every(isCategory))
    return { ok: false, message: 'Each category needs a code and label.' };
  const codes = new Set<string>();
  for (const category of categories) {
    const code = category.code.trim();
    const label = category.label.trim();
    if (!/^[A-Z][A-Z0-9_]*$/.test(code) || !label || label.length > 60 || codes.has(code)) {
      return {
        ok: false,
        message: 'Use unique stable codes and labels of 60 characters or fewer.',
      };
    }
    codes.add(code);
    const canonical = DEFAULT_COMPLAINT_CATEGORY_SET.categories.find((item) => item.code === code);
    if (!canonical && !code.startsWith('TENANT_')) {
      return { ok: false, message: 'Custom category codes must start with TENANT_.' };
    }
    if (code === 'OTHER' && (category.retired || label.toLowerCase() !== 'other')) {
      return { ok: false, message: 'Other is always available and keeps its clear label.' };
    }
    if (category.retired && (!category.replacementCode || category.replacementCode === code)) {
      return { ok: false, message: 'Choose a different replacement before retiring a category.' };
    }
  }
  for (const canonical of DEFAULT_COMPLAINT_CATEGORY_SET.categories) {
    if (!codes.has(canonical.code))
      return { ok: false, message: 'Keep every canonical category code.' };
  }
  for (const category of categories) {
    if (category.retired && !codes.has(category.replacementCode!)) {
      return { ok: false, message: 'A retired category replacement must remain available.' };
    }
  }
  return {
    ok: true,
    value: {
      categories: categories.map((category) => ({
        code: category.code.trim(),
        label: category.label.trim(),
        ...(category.retired ? { retired: true, replacementCode: category.replacementCode } : {}),
      })),
    },
  };
}

export const COMPLAINT_CATEGORY_LEGACY_LITERALS = [
  ...canonicalPairs.map(([, label]) => label),
  ...canonicalPairs.slice(0, 10).map(([code]) => code.toLowerCase()),
  'General',
] as const;
